import {Injectable} from "@angular/core";
import {HttpClient, HttpHeaders, HttpParams, HttpErrorResponse} from "@angular/common/http";
import {Observable, of, throwError} from "rxjs";
import {catchError, debounceTime, map, retry, timeout} from "rxjs/operators";
import {CookieService} from "ngx-cookie-service";
import {environment} from "../../../environments/environment";
import {env} from "process";
import {ApiError} from "../../models/types/api-error";
import {ApiResponse} from "../../models/types/api-response";

export interface IApiResponse<T> {
    data: IApiResponseResult<T> | null;
    responseCode?: number;
}

export interface IApiResponseResult<T> {
    readonly isSuccess: boolean;
    readonly isPartialSuccess: boolean;
    readonly isFailure: boolean;
    readonly hasErrors: boolean;
    readonly errors: IApiResponseResultError[] | null;
    readonly value: T | null;
}

export interface IApiResponseResultError {
    errorCode: string;
    errorDescription: string;
    errorDetails: string;
    applicationErrorCode: number;
}

export class ApiResponseResult<T> implements IApiResponseResult<T> {
    readonly value: T | null = null;
    readonly errors: IApiResponseResultError[] | null = [];
    readonly isSuccess: boolean = false;
    readonly isPartialSuccess: boolean = false;
    readonly isFailure: boolean = false;
    readonly hasErrors: boolean = false;

    constructor(
        value: T,
        errors: IApiResponseResultError[] | null,
        isSuccess: boolean,
        isPartialSuccess: boolean,
        isFailure: boolean,
        hasErrors: boolean
    ) {
        this.value = value;
        this.errors = errors;
        (this.isSuccess = isSuccess), (this.isPartialSuccess = isPartialSuccess);
        this.isFailure = isFailure;
        this.hasErrors = hasErrors;
    }
}

@Injectable({
    providedIn: "root"
})
export class HttpService {
    private readonly baseUrl = environment.apiUrl;
    private readonly defaultTimeout = 60000; // 60 seconds

    constructor(private http: HttpClient, private cookieService: CookieService) {}
    // GET request
    get<T>(endpoint: string, params?: any): Observable<T> {
        const httpParams = this.buildParams(params);

        return this.http
            .get<T>(`${this.baseUrl}${endpoint}`, {
                params: httpParams,
                headers: this.getHeaders()
            })
            .pipe(timeout(this.defaultTimeout), catchError(this.handleError));
    }

    // POST request
    post<T>(endpoint: string, body: any): Observable<T> {
        return this.http
            .post<T>(`${this.baseUrl}${endpoint}`, body, {
                headers: this.getHeaders()
            })
            .pipe(timeout(this.defaultTimeout), debounceTime(3000), catchError(this.handleError));
    }

    // PUT request
    put<T>(endpoint: string, body: any): Observable<T> {
        return this.http
            .put<T>(`${this.baseUrl}${endpoint}`, body, {
                headers: this.getHeaders()
            })
            .pipe(timeout(this.defaultTimeout), catchError(this.handleError));
    }

    // PATCH request
    patch<T>(endpoint: string, body: any): Observable<T> {
        return this.http
            .patch<T>(`${this.baseUrl}${endpoint}`, body, {
                headers: this.getHeaders()
            })
            .pipe(timeout(this.defaultTimeout), catchError(this.handleError));
    }

    // DELETE request
    delete<T>(endpoint: string): Observable<T> {
        return this.http
            .delete<T>(`${this.baseUrl}${endpoint}`, {
                headers: this.getHeaders()
            })
            .pipe(timeout(this.defaultTimeout), catchError(this.handleError));
    }

    //// File upload
    //uploadFile<T>(endpoint: string, file: File, additionalData?: any): Observable<T> {
    //  const formData = new FormData();
    //  formData.append('file', file);

    //  if (additionalData) {
    //    Object.keys(additionalData).forEach(key => {
    //      formData.append(key, additionalData[key]);
    //    });
    //  }

    //  return this.http.post<T>(`${this.baseUrl}${endpoint}`, formData, {
    //    headers: this.getFileUploadHeaders()
    //  }).pipe(
    //    timeout(60000), // Longer timeout for file uploads
    //    catchError(this.handleError)
    //  );
    //}

    //// Download file
    //downloadFile(endpoint: string, filename: string): Observable<Blob> {
    //  return this.http.get(`${this.baseUrl}${endpoint}`, {
    //    responseType: 'blob',
    //    headers: this.getHeaders()
    //  }).pipe(
    //    catchError(this.handleError)
    //  );
    //}

    // Private helper methods
    private getHeaders(): HttpHeaders {
        let headers = new HttpHeaders({
            "Content-Type": "application/json",
            "x-xsrf-token": this.cookieService.get("XSRF-TOKEN") || "" // Use cookie service to get CSRF token
        });

        // Add authorization token if available
        const token = this.getAuthToken();
        if (token) {
            headers = headers.set("Authorization", `Bearer ${token}`);
        }

        return headers;
    }

    //private getFileUploadHeaders(): HttpHeaders {
    //  let headers = new HttpHeaders();

    //  // Add authorization token if available
    //  const token = this.getAuthToken();
    //  if (token) {
    //    headers = headers.set('Authorization', `Bearer ${token}`);
    //  }

    //  // Note: Don't set Content-Type for file uploads, let browser set it
    //  return headers;
    //}

    private getAuthToken(): string | null {
        // Get token from localStorage, sessionStorage, or your auth service
        return localStorage.getItem("authToken");
    }

    private buildParams(params: any): HttpParams {
        let httpParams = new HttpParams();

        if (params) {
            Object.keys(params).forEach((key) => {
                if (params[key] !== null && params[key] !== undefined) {
                    httpParams = httpParams.set(key, params[key].toString());
                }
            });
        }

        return httpParams;
    }

    private handleError = <T>(error: HttpErrorResponse, caught: Observable<T>): Observable<T> => {
        let errorMessage = "An unknown error occurred";

        if (error.error instanceof ErrorEvent) {
            // Client-side error
            errorMessage = `Client Error: ${error.error.message}`;
            return throwError(() => caught);
        } else {
            // Server-side error
            //switch (error.status) {
            //  case 400:
            //    errorMessage = 'Bad Request - Please check your input';
            //    break;
            //  case 401:
            //    errorMessage = 'Unauthorized - Please login again';
            //    this.handleUnauthorized();
            //    break;
            //  case 403:
            //    errorMessage = 'Forbidden - You don\'t have permission';
            //    break;
            //  case 404:
            //    errorMessage = 'Not Found - The requested resource was not found';
            //    break;
            //  case 499:
            //    errorMessage = 'Too Many Requests - You have attempted to many requests. Please try again a few moments';
            //    break;
            //  case 429: errorMessage = 'Request Blocked - You have been restricted. Please try again later.'
            //    break;
            //  case 500:
            //    errorMessage = 'Internal Server Error - Please try again later';
            //    break;
            //  case 503:
            //    errorMessage = 'Service Unavailable - Server is temporarily down';
            //    break;
            //  default:
            //    errorMessage = `Server Error: ${error.status} - ${error.message}`;
            //}
            return throwError(() => caught);
        }
    };

    private handleUnauthorized(): void {
        // Clear auth token and redirect to login
        localStorage.removeItem("authToken");
        // You might want to inject Router and navigate to login page
        // this.router.navigate(['/login']);
    }
}

//// Example usage service that extends the base HTTP service
//@Injectable({
//  providedIn: 'root'
//})
//export class UserService {
//  private readonly endpoint = '/users';

//  constructor(private httpService: HttpService) { }

//  getUsers(page: number = 1, limit: number = 10): Observable<ApiResponse<any[]>> {
//    return this.httpService.get(this.endpoint, { page, limit });
//  }

//  getUserById(id: number): Observable<ApiResponse<any>> {
//    return this.httpService.get(`${this.endpoint}/${id}`);
//  }

//  createUser(userData: any): Observable<ApiResponse<any>> {
//    return this.httpService.post(this.endpoint, userData);
//  }

//  updateUser(id: number, userData: any): Observable<ApiResponse<any>> {
//    return this.httpService.put(`${this.endpoint}/${id}`, userData);
//  }

//  deleteUser(id: number): Observable<ApiResponse<any>> {
//    return this.httpService.delete(`${this.endpoint}/${id}`);
//  }

//  //uploadUserAvatar(id: number, file: File): Observable<ApiResponse<any>> {
//  //  return this.httpService.uploadFile(`${this.endpoint}/${id}/avatar`, file);
//  //}
//}
